// Stage 50: Gadfly Module — frontend API client + type re-declarations.
//
// Types are DELIBERATELY duplicated rather than imported from the worker.
// The cross-package air gap is duplication, mirroring the pattern Memory /
// Homunculus / Phantom use. See stage50-gadfly-integration-plan.md §24.
//
// IMPORTANT: scripts/verify-gadfly-boundaries.cjs asserts that
// frontend/src/components/ChatTranscript.tsx never imports this module.
// rawConfusionMd must never reach end users in the chat — it is
// operator-only diagnostic content.

import { apiGet, apiJson } from './client';

export type BiteKind = 'OBSERVATION' | 'QUESTION' | 'OBJECTION';

export interface Bite {
  index: number;
  kind: BiteKind;
  text: string;
  rawLine: string;
}

export type SwatTag = 'ACCEPT' | 'REJECT' | 'DEFER';

export interface SwatEntry {
  biteIndex: number;
  tag: SwatTag;
  reason: string;
  taggedAt: string;
}

export interface StripeRatio {
  keep: number;
  cut: number;
}

export interface GadflyHealth {
  ok: boolean;
  service: string;
  version: string;
  enabled: boolean;
  model: string;
  apiKeyConfigured: boolean;
  region: string;
  timestamp: string;
}

export interface BiteRequest {
  context: string;
  conversationId: string;
  turnId: string;
  options?: {
    stripeRatio?: StripeRatio;
    temperature?: number;
    model?: string;
    maxOutputTokens?: number;
    timeoutMs?: number;
  };
}

export interface BiteResponse {
  bites: Bite[];
  rawConfusionMd: string;
  generatedAt: string;
  durationMs: number;
  stripeRatioApplied: StripeRatio;
  modelUsed: string;
  failed: boolean;
  failureReason: string | null;
}

/** GET /api/gadfly/healthcheck — public, no auth. */
export const gadflyHealthcheck = () =>
  apiGet<GadflyHealth>('/api/gadfly/healthcheck');

/** POST /api/gadfly/bite — admin-only. Used by the "Test bite" affordance. */
export const gadflyBite = (input: BiteRequest) =>
  apiJson<BiteResponse>('POST', '/api/gadfly/bite', input);
